/** Rendering bands that every team agrees on */
export enum MapLayerCategory {
  Base        = 'base',        // basemap, hillshade …
  Overlays    = 'overlays',    // DEM raster, contours, weather …
  BaseAnnotations = 'base-annotations',
  Annotations = 'annotations', // trails, polygons, markers …
  Top         = 'top'          // user location icon, pop-ups …
}

/** Hard ordering between categories               0 ← back … front → 3 */
const CATEGORY_RANK: Record<MapLayerCategory, number> = {
  [MapLayerCategory.Base]:        0,
  [MapLayerCategory.Overlays]:    1,
  [MapLayerCategory.BaseAnnotations]: 2,
  [MapLayerCategory.Annotations]: 3,
  [MapLayerCategory.Top]:         4
};

/** Mapbox GL JS v3 ‘slot’ that each category lives in */
const CATEGORY_SLOT: Record<MapLayerCategory, 'bottom'|'middle'|'top'> = {
  [MapLayerCategory.Base]:        'bottom',
  [MapLayerCategory.Overlays]:    'middle',
  [MapLayerCategory.BaseAnnotations]: 'middle',
  [MapLayerCategory.Annotations]: 'middle',
  [MapLayerCategory.Top]:         'top'
};

/** Any Mapbox-GL source spec (vector, raster-dem, geojson, …) */
export type SourceSpec = mapboxgl.AnySourceData;

/** Any Mapbox-GL layer spec (fill, line, symbol, raster, …) */
export type LayerSpec  = mapboxgl.AnyLayer;

/** Registry keeps a record of what it added so it can replay later */
interface Registered {
  sourceId: string;
  layerId:  string;
  category: MapLayerCategory;
  z:        number;
}

export class LayerRegistry {
  private map: mapboxgl.Map;
  private added = new Map<string, Registered>(); // layerId ➜ meta

  constructor(map: mapboxgl.Map) {
    this.map = map;
    map.on('style.load', () => this.replay());
  }

  /* ---------- public API ---------- */

  /**
   * Add *only* a source. If the id already exists:
   *   – replace = true  ➜  remove & re-add
   *   – replace = false ➜  no-op
   */
  addSource(id: string, spec: SourceSpec, replace = false) {
    if (this.map.getSource(id)) {
      if (!replace) return;

      // --- NEW: Remove layers managed by this registry that use this source ---
      const layersToRemove: string[] = [];
      // @ts-ignore
      for (const [layerId, registration] of this.added.entries()) {
        if (registration.sourceId === id) {
          layersToRemove.push(layerId);
        }
      }

      layersToRemove.forEach(layerId => {
        // Remove layer from map
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
        // Remove layer from registry's tracking
        this.added.delete(layerId);
      });
      // --- End NEW ---

      // Now it should be safe to remove the source
      this.map.removeSource(id);
    }
    this.map.addSource(id, spec);
  }

  /**
   * Add a layer and (optionally) ensure its source exists.
   * If a layer with the same id already exists:
   *   – replace = true  ➜  remove & re‑add
   *   – replace = false ➜  no‑op
   */
  addLayer(
    layer: LayerSpec,
    category: MapLayerCategory,
    opts: {
      z?: number;
      replace?: boolean; // NEW
      source?: { id: string; spec: SourceSpec; replace?: boolean };
    } = {}
  ) {
    const { z = 0, source, replace = false } = opts;

    /* 0 ─ early‑out or remove existing layer if requested */
    const already = this.map.getLayer(layer.id);
    if (already) {
      if (!replace) return; // nothing to do
      this.removeLayer(layer.id, /*removeOrphanSource*/ false);
    }

    /* 1 ─ ensure source exists */
    if (source) this.addSource(source.id, source.spec, source.replace);
    if (layer.source && !this.map.getSource(layer.source as string)) {
      throw new Error(`Source “${layer.source}” missing for layer “${layer.id}”`);
    }

    /* 2 ─ tag metadata so we can replay after a style swap */
    layer.metadata = { category, z };

    /* 3 ─ strip ‘slot’ left over from helpers written for v3 */
    const cleanLayer = { ...layer };
    delete (cleanLayer as any).slot;

    /* 4 ─ insert in deterministic order  */
    this.map.addLayer(cleanLayer, this.findBeforeId(category, z));

    /* 5 ─ remember it */
    this.added.set(layer.id, {
      sourceId: cleanLayer.source as string,
      layerId:  cleanLayer.id,
      category,
      z
    });
  }

  /** Remove one layer (and — optionally — its source if now unused) */
  removeLayer(id: string, removeOrphanSource = true) {
    // Only attempt if the layer is present to avoid Mapbox errors
    const existsOnMap = !!this.map.getLayer(id);
    if (!existsOnMap) return;

    const meta = this.added.get(id);

    this.map.removeLayer(id);
    if (meta) this.added.delete(id);

    if (removeOrphanSource && meta) {
      const { sourceId } = meta;
      // @ts-ignore
      const sourceStillUsed = [...this.added.values()].some(r => r.sourceId === sourceId);
      if (!sourceStillUsed && this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    }
  }

  /** Update the data/url of an existing source */ // @ts-ignore
  updateSource(id: string, mutator: (s: mapboxgl.AnySource) => void) {
    const src = this.map.getSource(id);
    if (!src) throw new Error(`Source “${id}” not found`);
    mutator(src);
  }

  /* ---------- internals ---------- */

  /** Re-add + re-sort everything after a style change */
  private replay() {
    // @ts-ignore
    [...this.added.values()]
      .sort((a, b) =>
        CATEGORY_RANK[a.category] - CATEGORY_RANK[b.category] ||
        a.z - b.z)
      .forEach(({ layerId, category, z }) =>
        // Only move if the layer still exists (may have been removed since)
        this.map.getLayer(layerId) &&
        this.map.moveLayer(layerId, this.findBeforeId(category, z)));
  }

  /** Find the layer id that should come *after* (category,z) */
  private findBeforeId(cat: MapLayerCategory, z: number) {
    return this.map.getStyle().layers?.find(l => {
      const m = l.metadata ?? {};
      const catCmp =
        // @ts-ignore
        CATEGORY_RANK[m.category as MapLayerCategory ?? MapLayerCategory.Base] -
        CATEGORY_RANK[cat];
      // @ts-ignore
      return catCmp > 0 || (catCmp === 0 && (m.z ?? 0) > z);
    })?.id;
  }
}
